/**
 * Feed packages — the entitlement products.
 *
 * A package grants a set of feed keys. A client is entitled to those feeds by
 * holding a delivery assignment served under the package (managed on the
 * client's page). This screen is where the products themselves — and the feed
 * keys each one grants — are created and edited.
 */

import { useState } from "react";

import {
  Button,
  Card,
  CheckboxField,
  Empty,
  Field,
  FieldRow,
  Input,
  PageHead,
  Pill,
  Problem,
  Section,
  Table,
  Td,
  Textarea,
  Th,
} from "@/components/kit";
import {
  createPackageAction,
  updateFeedDefinitionAction,
  updatePackageAction,
} from "@/lib/actions/delivery";
import {
  ApiError,
  getFeedDefinition,
  listFeedPackages,
  previewFeedDefinition,
} from "@/lib/api/client";
import type { FeedDefinition, FeedPackage, FeedPreviewResult } from "@/lib/api/types";
import { errorText, useApi } from "@/spa/useApi";

/** Feed keys are edited as a comma/space/newline separated list. */
function parseKeys(raw: string): string[] {
  return raw
    .split(/[\s,]+/)
    .map((k) => k.trim())
    .filter(Boolean);
}

function AddPackage() {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [keys, setKeys] = useState("");
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    const result = await createPackageAction({
      code: code.trim(),
      name: name.trim(),
      feedKeys: parseKeys(keys),
    });
    setBusy(false);
    if (result) {
      setError(result.violations.length ? result.violations.join("; ") : result.error);
      return;
    }
    setCode("");
    setName("");
    setKeys("");
  }

  return (
    <Section
      title="Add a package"
      note="A package grants the feed keys you list here — e.g. calendar, medals_tally."
    >
      <form onSubmit={submit}>
        <FieldRow>
          <Field label="Code" required hint="asian-games, base" htmlFor="p-code">
            <Input
              id="p-code"
              value={code}
              required
              placeholder="asian-games"
              onChange={(e) => setCode(e.target.value)}
            />
          </Field>
          <Field label="Name" required htmlFor="p-name">
            <Input
              id="p-name"
              value={name}
              required
              placeholder="Asian Games"
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
        </FieldRow>
        <Field
          label="Feed keys"
          hint="Separate with commas or spaces — calendar, medals_tally, results"
          htmlFor="p-keys"
        >
          <Input
            id="p-keys"
            value={keys}
            placeholder="calendar, medals_tally"
            onChange={(e) => setKeys(e.target.value)}
          />
        </Field>
        {error && (
          <p style={{ color: "var(--bad)", fontSize: "var(--t-sm)" }} role="alert">
            {error}
          </p>
        )}
        <div style={{ marginTop: "var(--s3)" }}>
          <Button tone="primary" type="submit" disabled={busy || !code.trim() || !name.trim()}>
            {busy ? "Adding…" : "Add package"}
          </Button>
        </div>
      </form>
    </Section>
  );
}

function PackageEdit({ pkg, onDone }: { pkg: FeedPackage; onDone: () => void }) {
  const [name, setName] = useState(pkg.name);
  const [keys, setKeys] = useState(pkg.feedKeys.join(", "));
  const [active, setActive] = useState(pkg.isActive);
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    setError(undefined);
    const result = await updatePackageAction(pkg.id, {
      name: name.trim(),
      feedKeys: parseKeys(keys),
      isActive: active,
    });
    setBusy(false);
    if (result) {
      setError(result.violations.length ? result.violations.join("; ") : result.error);
      return;
    }
    onDone();
  }

  return (
    <tr>
      <Td>
        <Input value={name} onChange={(e) => setName(e.target.value)} />
      </Td>
      <Td quiet>{pkg.code}</Td>
      <Td>
        <Input value={keys} onChange={(e) => setKeys(e.target.value)} />
        {error && (
          <span style={{ color: "var(--bad)", fontSize: "var(--t-sm)" }} role="alert">
            {error}
          </span>
        )}
      </Td>
      <Td>
        <CheckboxField label="Active" checked={active} onChange={(e) => setActive(e.target.checked)} />
      </Td>
      <Td>
        <Button tone="primary" onClick={save} disabled={busy}>
          {busy ? "Saving…" : "Save"}
        </Button>{" "}
        <Button onClick={onDone} disabled={busy}>
          Cancel
        </Button>
      </Td>
    </tr>
  );
}

/**
 * The GraphQL editor + live preview for one feed definition, loaded by code.
 *
 * The query on `feed_definition` is config, not code — editing it changes what
 * the feed selects with no deploy. Save round-trips through the admin API;
 * Preview runs the (possibly unsaved-in-the-DB, but here the saved) query
 * against the live public API with the same variables the assembler binds, so
 * an editor sees what a client would receive. An empty/`null` result is a valid
 * preview — the feed simply has no rows for that event yet.
 */
function LoadedFeedEditor({ def }: { def: FeedDefinition }) {
  const [query, setQuery] = useState(def.query);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | undefined>();
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState<FeedPreviewResult | undefined>();
  const [previewErr, setPreviewErr] = useState<string | undefined>();
  const dirty = query !== def.query;

  async function save() {
    setSaving(true);
    setSaveErr(undefined);
    const result = await updateFeedDefinitionAction(def.code, { query });
    setSaving(false);
    if (result) setSaveErr(result.violations.length ? result.violations.join("; ") : result.error);
  }

  async function runPreview() {
    setPreviewing(true);
    setPreviewErr(undefined);
    setPreview(undefined);
    try {
      setPreview(await previewFeedDefinition(def.code));
    } catch (error) {
      setPreviewErr(errorText(error, "preview failed"));
    } finally {
      setPreviewing(false);
    }
  }

  const variant = [def.feedType, def.filter || "base", def.team, def.top && `top ${def.top}`]
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      style={{
        border: "1px solid var(--line)",
        borderRadius: "var(--r2)",
        padding: "var(--s3)",
        display: "grid",
        gap: "var(--s2)",
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: "var(--s2)", flexWrap: "wrap" }}>
        <strong>{def.name}</strong>
        <code style={{ color: "var(--muted)", fontSize: "var(--t-sm)" }}>{def.code}</code>
        <span style={{ color: "var(--muted)", fontSize: "var(--t-sm)" }}>{variant}</span>
        <Pill tone={def.enabled ? "good" : "neutral"} dot>
          {def.enabled ? "Enabled" : "Disabled"}
        </Pill>
      </div>
      <Textarea
        aria-label={`GraphQL query for ${def.code}`}
        value={query}
        spellCheck={false}
        rows={Math.min(24, Math.max(8, query.split("\n").length + 1))}
        onChange={(e) => setQuery(e.target.value)}
        style={{
          fontFamily: "var(--mono, ui-monospace, SFMono-Regular, Menlo, monospace)",
          fontSize: "var(--t-sm)",
          whiteSpace: "pre",
          overflowWrap: "normal",
          resize: "vertical",
        }}
      />
      {saveErr && (
        <p style={{ color: "var(--bad)", fontSize: "var(--t-sm)" }} role="alert">
          {saveErr}
        </p>
      )}
      <div style={{ display: "flex", gap: "var(--s2)", alignItems: "center" }}>
        <Button tone="primary" onClick={save} disabled={saving || !dirty}>
          {saving ? "Saving…" : dirty ? "Save query" : "Saved"}
        </Button>
        <Button onClick={runPreview} disabled={previewing}>
          {previewing ? "Running…" : "Preview"}
        </Button>
        {dirty && (
          <span style={{ color: "var(--muted)", fontSize: "var(--t-sm)" }}>
            Preview runs the saved query — save first to preview edits.
          </span>
        )}
      </div>
      {previewErr && (
        <p style={{ color: "var(--bad)", fontSize: "var(--t-sm)" }} role="alert">
          {previewErr}
        </p>
      )}
      {preview && (
        <div style={{ display: "grid", gap: "var(--s2)" }}>
          <p style={{ color: "var(--muted)", fontSize: "var(--t-sm)" }}>
            event <code>{preview.event}</code> · variables{" "}
            <code>{JSON.stringify(preview.variables)}</code>
          </p>
          {preview.errors ? (
            <>
              <span style={{ color: "var(--bad)", fontSize: "var(--t-sm)" }}>GraphQL errors:</span>
              <pre
                style={{
                  margin: 0,
                  padding: "var(--s2)",
                  background: "var(--surface, rgba(0,0,0,0.04))",
                  borderRadius: "var(--r2)",
                  overflow: "auto",
                  maxHeight: 320,
                  fontSize: "var(--t-sm)",
                  color: "var(--bad)",
                }}
              >
                {JSON.stringify(preview.errors, null, 2)}
              </pre>
            </>
          ) : (
            <pre
              style={{
                margin: 0,
                padding: "var(--s2)",
                background: "var(--surface, rgba(0,0,0,0.04))",
                borderRadius: "var(--r2)",
                overflow: "auto",
                maxHeight: 320,
                fontSize: "var(--t-sm)",
              }}
            >
              {JSON.stringify(preview.data, null, 2)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

/** Load one definition by its feed key (the definition `code`) and edit it. */
function FeedDefinitionEditor({ code }: { code: string }) {
  const { data: def, loading, error, cause } = useApi(
    () => getFeedDefinition(code),
    [code],
    "failed to load feed",
  );
  if (error) {
    const notFound = cause instanceof ApiError && cause.status === 404;
    return (
      <p style={{ color: notFound ? "var(--muted)" : "var(--bad)", fontSize: "var(--t-sm)" }}>
        <code>{code}</code> — {notFound ? "no feed definition for this key" : error}
      </p>
    );
  }
  if (!def) return <p style={{ color: "var(--muted)" }}>{loading ? `Loading ${code}…` : "No data."}</p>;
  return <LoadedFeedEditor def={def} />;
}

/** The feeds a package grants, each as an editable/previewable definition. */
function FeedsPanel({ pkg }: { pkg: FeedPackage }) {
  if (pkg.feedKeys.length === 0) {
    return <p style={{ color: "var(--muted)" }}>This package grants no feeds yet.</p>;
  }
  return (
    <div style={{ display: "grid", gap: "var(--s3)" }}>
      {pkg.feedKeys.map((code) => (
        <FeedDefinitionEditor key={code} code={code} />
      ))}
    </div>
  );
}

function PackageRow({ pkg }: { pkg: FeedPackage }) {
  const [editing, setEditing] = useState(false);
  const [showFeeds, setShowFeeds] = useState(false);
  if (editing) return <PackageEdit pkg={pkg} onDone={() => setEditing(false)} />;
  return (
    <>
      <tr>
        <Td strong>{pkg.name}</Td>
        <Td quiet>{pkg.code}</Td>
        <Td>
          {pkg.feedKeys.length === 0 ? (
            <span style={{ color: "var(--muted)" }}>none</span>
          ) : (
            <span style={{ display: "inline-flex", flexWrap: "wrap", gap: "var(--s2)" }}>
              {pkg.feedKeys.map((k) => (
                <Pill key={k}>{k}</Pill>
              ))}
            </span>
          )}
        </Td>
        <Td>
          <Pill tone={pkg.isActive ? "good" : "neutral"} dot>
            {pkg.isActive ? "Active" : "Inactive"}
          </Pill>
        </Td>
        <Td>
          <Button onClick={() => setShowFeeds((v) => !v)}>
            {showFeeds ? "Hide feeds" : "Edit feeds"}
          </Button>{" "}
          <Button onClick={() => setEditing(true)}>Edit</Button>
        </Td>
      </tr>
      {showFeeds && (
        <tr>
          <Td colSpan={5} style={{ background: "var(--surface-sunken, transparent)" }}>
            <FeedsPanel pkg={pkg} />
          </Td>
        </tr>
      )}
    </>
  );
}

export function PackagesPage() {
  const { data: rows, loading, error } = useApi(listFeedPackages, [], "failed to load");

  if (error) return <Problem>Could not reach the admin service: {error}</Problem>;

  return (
    <>
      <PageHead
        title="Feed packages"
        note="The entitlement products — each grants a set of feed keys."
      />

      {!rows ? (
        <Card>{loading ? "Loading…" : "No data."}</Card>
      ) : rows.length === 0 ? (
        <Card>
          <Empty title="No packages yet" note="Add the first product below." />
        </Card>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Name</Th>
              <Th>Code</Th>
              <Th>Feed keys</Th>
              <Th>Status</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <PackageRow key={p.id} pkg={p} />
            ))}
          </tbody>
        </Table>
      )}

      <AddPackage />
    </>
  );
}
